import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/app/utils/i18n';
import { useAqlOperationalContext } from './AqlOperationalContextProvider';
import {
  sendAqlAssistantQuestion,
  type AqlAssistantCard,
  type AqlAssistantContext,
  type AqlAssistantEvidence,
  type AqlAssistantResponse,
  type AqlAssistantUsage
} from './assistantApi';

export interface AqlAssistantLocalSummary {
  activeAlerts?: number;
  activeCriticalAlerts?: number;
  sensorOutOfRangeCount?: number;
  staleSensorCount?: number;
  deviceOfflineCount?: number;
  growthWarningLevel?: 'none' | 'normal' | 'warning' | 'critical' | null;
  lowMeasurementConfidence?: boolean;
  recentPathologyCases?: number;
}

export interface AqlAssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  cards?: AqlAssistantCard[];
  evidence?: AqlAssistantEvidence[];
}

export interface AqlAssistantTokenBudget {
  provider: string;
  model: string;
  used: number;
  budget: number;
  lastRequest: number;
}

const DEFAULT_TOKEN_BUDGET = 1_000_000;

const ptQuestions = {
  dashboard: [
    'O que requer atenção neste momento?',
    'Resume o risco operacional atual.',
    'Qual kit ou tanque tem maior risco?'
  ],
  alerts: [
    'Porque foram estes alertas acionados?',
    'Que alertas devo validar primeiro?',
    'Mostra alertas críticos de patologia ou sensores por resolver.'
  ],
  analyses: [
    'Explica os últimos 30 dias de deteções por IA.',
    'A confiança do modelo está a melhorar ou a degradar?',
    'Que padrões são visíveis nas suspeitas de patologia?'
  ],
  growth: [
    'Esta coorte está a crescer como esperado?',
    'Explica a tendência de crescimento e a qualidade da amostra.',
    'As medições rejeitadas estão a afetar a fiabilidade?'
  ],
  camera_live: [
    'Explica o que a última captura indica.',
    'Os sensores e as deteções visuais estão alinhados?',
    'Isto é uma anomalia visual ou um risco ambiental?'
  ]
};

const enQuestions: typeof ptQuestions = {
  dashboard: [
    'What requires attention right now?',
    'Summarize the current operational risk.',
    'Which kit or tank has the highest risk?'
  ],
  alerts: [
    'Why were these alerts triggered?',
    'Which alerts should be validated first?',
    'Show unresolved critical pathology or sensor alerts.'
  ],
  analyses: [
    'Explain the last 30 days of AI detections.',
    'Is the model confidence improving or degrading?',
    'What patterns are visible in pathology detections?'
  ],
  growth: [
    'Is this cohort growing as expected?',
    'Explain the growth trend and sample quality.',
    'Are rejected measurements affecting reliability?'
  ],
  camera_live: [
    'Explain what the latest capture indicates.',
    'Are sensors and visual detections aligned?',
    'Is this a visual anomaly or an environmental risk?'
  ]
};

const uniqueThree = (questions: string[]) => Array.from(new Set(questions)).slice(0, 3);

export function generateSuggestedQuestions(
  context: AqlAssistantContext,
  summary?: AqlAssistantLocalSummary
) {
  const isPt = (context.language ?? '').startsWith('pt');
  const base = isPt ? ptQuestions : enQuestions;
  const pageQuestions = [...base[context.current_page]];

  if (context.current_page === 'growth' && summary?.growthWarningLevel && !['none', 'normal'].includes(summary.growthWarningLevel)) {
    return uniqueThree(isPt
      ? [
          'Porque é que o crescimento está em aviso?',
          'A amostra é suficientemente fiável?',
          'Que medições foram rejeitadas e porquê?'
        ]
      : [
          'Why is growth in warning?',
          'Is the sample reliable enough?',
          'Which measurements were rejected and why?'
        ]);
  }

  if (context.current_page === 'alerts' && (summary?.activeCriticalAlerts ?? 0) > 0) {
    return uniqueThree(isPt
      ? [
          'Qual destes alertas é mais urgente?',
          'Há ligação entre sensores fora do alvo e suspeitas visuais?',
          'Que ações devo tomar primeiro?'
        ]
      : [
          'Which alert is most urgent?',
          'Is there a link between out-of-range sensors and visual suspicions?',
          'What actions should I take first?'
        ]);
  }

  if ((summary?.deviceOfflineCount ?? 0) > 0) {
    pageQuestions.unshift(isPt ? 'Que equipamento está offline e qual é o impacto?' : 'Which equipment is offline and what is the impact?');
  }

  if ((summary?.sensorOutOfRangeCount ?? 0) > 0) {
    pageQuestions.unshift(isPt ? 'Que sensores estão fora do alvo e que risco criam?' : 'Which sensors are out of range and what risk do they create?');
  }

  if ((summary?.activeCriticalAlerts ?? 0) > 0) {
    pageQuestions.unshift(isPt ? 'O que devo validar primeiro nos alertas críticos?' : 'What should I validate first in the critical alerts?');
  }

  if (summary?.lowMeasurementConfidence) {
    pageQuestions.unshift(isPt ? 'A confiança da amostra é suficiente para decidir?' : 'Is sample confidence sufficient for a decision?');
  }

  return uniqueThree(pageQuestions);
}

const createMessageId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const tokenBudgetStorageKey = (context: AqlAssistantContext) => [
  'aql-assistant-token-budget',
  context.client_id ?? 'global',
  context.project_id ?? 'all',
  context.current_page ?? 'dashboard'
].join(':');

const readTokenBudget = (context: AqlAssistantContext): AqlAssistantTokenBudget => {
  const fallback: AqlAssistantTokenBudget = {
    provider: 'gemini',
    model: 'gemini-2.0-flash',
    used: 0,
    budget: DEFAULT_TOKEN_BUDGET,
    lastRequest: 0
  };
  if (typeof localStorage === 'undefined') return fallback;
  try {
    const stored = JSON.parse(localStorage.getItem(tokenBudgetStorageKey(context)) ?? 'null');
    return {
      provider: typeof stored?.provider === 'string' ? stored.provider : fallback.provider,
      model: typeof stored?.model === 'string' ? stored.model : fallback.model,
      used: Number.isFinite(Number(stored?.used)) ? Math.max(0, Number(stored.used)) : fallback.used,
      budget: Number.isFinite(Number(stored?.budget)) ? Math.max(1, Number(stored.budget)) : fallback.budget,
      lastRequest: Number.isFinite(Number(stored?.lastRequest)) ? Math.max(0, Number(stored.lastRequest)) : fallback.lastRequest
    };
  } catch {
    return fallback;
  }
};

const writeTokenBudget = (context: AqlAssistantContext, budget: AqlAssistantTokenBudget) => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(tokenBudgetStorageKey(context), JSON.stringify(budget));
};

const usageToBudget = (current: AqlAssistantTokenBudget, usage?: AqlAssistantUsage): AqlAssistantTokenBudget => {
  const requestTokens = Math.max(0, Number(usage?.total_tokens ?? 0));
  const budget = Math.max(1, Number(usage?.budget_tokens ?? current.budget ?? DEFAULT_TOKEN_BUDGET));
  return {
    provider: String(usage?.provider ?? current.provider ?? 'gemini'),
    model: String(usage?.model ?? current.model ?? 'gemini-2.0-flash'),
    used: Math.min(budget, Math.max(0, current.used + requestTokens)),
    budget,
    lastRequest: requestTokens
  };
};

export function useAqlAssistant(params: {
  authToken?: string | null;
  summary?: AqlAssistantLocalSummary;
  demoMode?: boolean;
}) {
  const language = useLanguage();
  const baseContext = useAqlOperationalContext();
  const context = useMemo(() => ({ ...baseContext, language }), [baseContext, language]);
  const [messages, setMessages] = useState<AqlAssistantMessage[]>([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState(() => generateSuggestedQuestions(context, params.summary));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenBudget, setTokenBudget] = useState<AqlAssistantTokenBudget>(() => readTokenBudget(context));

  useEffect(() => {
    setTokenBudget(readTokenBudget(context));
  }, [context.client_id, context.project_id, context.current_page]);

  const resetSuggestions = useCallback(() => {
    setSuggestedQuestions(generateSuggestedQuestions(context, params.summary));
  }, [context, params.summary]);

  useEffect(() => {
    setSuggestedQuestions(generateSuggestedQuestions(context, params.summary));
  }, [context, params.summary]);

  const submitQuestion = useCallback(async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || loading) return;
    if (tokenBudget.used >= tokenBudget.budget) {
      setError(language === 'pt'
        ? 'O orçamento de 1.000.000 tokens do assistente foi atingido neste contexto.'
        : 'The assistant token budget of 1,000,000 tokens has been reached for this context.');
      return;
    }

    setError(null);
    setLoading(true);
    const userMessage: AqlAssistantMessage = {
      id: createMessageId(),
      role: 'user',
      content: trimmed,
      createdAt: new Date().toISOString()
    };
    setMessages((current) => [...current, userMessage]);

    try {
      const response: AqlAssistantResponse = params.demoMode
        ? buildDemoAssistantResponse(trimmed, context, params.summary)
        : await sendAqlAssistantQuestion({
            question: trimmed,
            context,
            authToken: params.authToken
          });

      const assistantMessage: AqlAssistantMessage = {
        id: createMessageId(),
        role: 'assistant',
        content: response.answer,
        createdAt: new Date().toISOString(),
        cards: response.cards,
        evidence: response.evidence
      };

      setMessages((current) => [...current, assistantMessage]);
      setTokenBudget((current) => {
        const next = usageToBudget(current, response.usage);
        writeTokenBudget(context, next);
        return next;
      });
      setSuggestedQuestions(
        response.suggested_questions.length
          ? response.suggested_questions.slice(0, 3)
          : generateSuggestedQuestions(context, params.summary)
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Assistant request failed.';
      const authLikeError = /sess[aã]o|session|auth|jwt|login|authenticated/i.test(message);
      setError(message);
      setMessages((current) => [
        ...current,
        {
          id: createMessageId(),
          role: 'assistant',
          content: authLikeError
            ? (language === 'pt'
              ? 'A sessão do assistente não está válida. Faz refresh ou entra novamente para renovar a autenticação.'
              : 'The assistant session is not valid. Refresh or sign in again to renew authentication.')
            : (language === 'pt'
              ? 'Não consegui obter uma resposta do backend do assistente. Tenta novamente quando a ligação estiver disponível.'
              : 'I could not get a response from the assistant backend. Please try again when the connection is available.'),
          createdAt: new Date().toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  }, [context, language, loading, params.authToken, params.demoMode, params.summary, tokenBudget.budget, tokenBudget.used]);

  return {
    context,
    messages,
    suggestedQuestions,
    loading,
    error,
    tokenBudget,
    resetSuggestions,
    submitQuestion
  };
}

function buildDemoAssistantResponse(
  question: string,
  context: AqlAssistantContext,
  summary?: AqlAssistantLocalSummary
): AqlAssistantResponse {
  const isPt = (context.language ?? '').startsWith('pt');
  const activeAlerts = summary?.activeAlerts ?? 0;
  const criticalAlerts = summary?.activeCriticalAlerts ?? 0;
  const outOfRangeSensors = summary?.sensorOutOfRangeCount ?? 0;
  const staleSensors = summary?.staleSensorCount ?? 0;
  const offlineDevices = summary?.deviceOfflineCount ?? 0;
  const lowConfidence = Boolean(summary?.lowMeasurementConfidence);
  const riskLevel = criticalAlerts > 0 ? 'critical' : outOfRangeSensors > 0 || offlineDevices > 0 || lowConfidence ? 'warning' : 'info';

  const answer = isPt
    ? `Risco operacional: ${riskLevel === 'critical' ? 'crítico' : riskLevel === 'warning' ? 'em aviso' : 'informativo'}.
- Alertas ativos: ${activeAlerts}; críticos: ${criticalAlerts}.
- Sensores fora do alvo: ${outOfRangeSensors}; sem leitura recente: ${staleSensors}.
- Próximo passo: validar críticos e confirmar correlação com sensores antes de decisão clínica.`
    : `Operational risk: ${riskLevel}.
- Active alerts: ${activeAlerts}; critical: ${criticalAlerts}.
- Out-of-range sensors: ${outOfRangeSensors}; stale: ${staleSensors}.
- Next step: validate critical items and check sensor correlation before clinical decisions.`;

  return {
    answer,
    cards: [
      {
        title: isPt ? 'Alertas ativos' : 'Active alerts',
        value: String(activeAlerts),
        severity: criticalAlerts > 0 ? 'critical' : activeAlerts > 0 ? 'warning' : 'success',
        explanation: isPt ? `${criticalAlerts} críticos no resumo atual.` : `${criticalAlerts} critical in the current summary.`
      },
      {
        title: isPt ? 'Sensores' : 'Sensors',
        value: `${outOfRangeSensors} out`,
        severity: outOfRangeSensors > 0 || staleSensors > 0 ? 'warning' : 'success',
        explanation: isPt ? `${staleSensors} sensores sem leitura recente.` : `${staleSensors} stale sensors.`
      }
    ],
    evidence: [
      {
        label: isPt ? 'Resumo operacional demo' : 'Demo operational summary',
        table: 'demo_operational_summary',
        record_id: null,
        timestamp: new Date().toISOString()
      }
    ],
    suggested_questions: generateSuggestedQuestions(context, summary),
    usage: {
      provider: 'demo',
      model: 'local-summary',
      prompt_tokens: Math.ceil(question.length / 4),
      completion_tokens: Math.ceil(answer.length / 4),
      total_tokens: Math.ceil((question.length + answer.length) / 4),
      budget_tokens: DEFAULT_TOKEN_BUDGET
    }
  };
}
